import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, message, Modal, Form, Input, InputNumber, Switch, Upload, Space, Typography, Popconfirm } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { bannerApi, uploadApi } from '../../services/api';

const { Title } = Typography;

// 图片上传（COS）
const BannerUploader = ({ value = {}, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await uploadApi.uploadImage(formData);
      onChange({ url: res.data.url });
      message.success('图片上传成功');
      onSuccess(res);
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload accept="image/jpeg,image/png,image/webp" listType="picture-card" showUploadList={false} customRequest={customRequest}>
      {value?.url ? (
        <img src={value.url} alt="轮播图" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>{uploading ? '上传中...' : '上传图片'}</div>
        </div>
      )}
    </Upload>
  );
};

const Banners = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await bannerApi.list();
      setData(res.data || []);
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true, sortOrder: 0 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      image: record.image_url ? { url: record.image_url } : {},
      linkPath: record.link_path,
      sortOrder: record.sort_order,
      status: record.status === 1,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const payload = {
        imageUrl: values.image?.url,
        linkPath: values.linkPath || undefined,
        sortOrder: values.sortOrder,
        status: values.status ? 1 : 0,
      };
      if (editing) {
        await bannerApi.update(editing.id, payload);
      } else {
        await bannerApi.create(payload);
      }
      message.success(editing ? '更新成功' : '添加成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await bannerApi.remove(id);
      message.success('已删除');
      fetchData();
    } catch (err) {
      if (!err.response) message.error('网络错误，请重试');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '图片', dataIndex: 'image_url', width: 160,
      render: (v) => v ? <img src={v} alt="" style={{ width: 140, height: 78, objectFit: 'cover' }} /> : '-'
    },
    { title: '跳转路径', dataIndex: 'link_path', render: (v) => v || '-' },
    { title: '排序', dataIndex: 'sort_order', width: 70 },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v) => v === 1 ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>
    },
    {
      title: '操作', width: 140, render: (_, r) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该轮播图？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>首页轮播图</Title>
        <Button type="primary" icon={<PlusOutlined />} style={{ background: '#A98E4E' }} onClick={openCreate}>
          添加轮播图
        </Button>
      </div>

      <Table columns={columns} dataSource={data} rowKey="id" loading={loading}
        pagination={false} />

      <Modal
        title={editing ? '编辑轮播图' : '添加轮播图'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { background: '#A98E4E' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="image" label="图片（建议 1125×700 竖版）" rules={[{ required: true, message: '请上传图片' }]}>
            <BannerUploader />
          </Form.Item>
          <Form.Item name="linkPath" label="点击跳转路径（可选）" rules={[{ max: 255 }]}
            extra="如 /pages/stores/stores（门店页）；留空则点击无跳转">
            <Input placeholder="/pages/stores/stores" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序" extra="越小越靠前">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Banners;
